import type { ReactNode } from "react";
import { fnum, fpct } from "../lib/format.js";
import { useChartTip } from "./ChartTip.js";

export interface Segment {
  label: string;
  value: number;
  color: string;
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arcPath(cx: number, cy: number, rOut: number, rIn: number, a0: number, a1: number): string {
  if (a1 - a0 >= 359.999) a1 = a0 + 359.999;
  const [x0, y0] = polar(cx, cy, rOut, a0);
  const [x1, y1] = polar(cx, cy, rOut, a1);
  const [x2, y2] = polar(cx, cy, rIn, a1);
  const [x3, y3] = polar(cx, cy, rIn, a0);
  const big = a1 - a0 > 180 ? 1 : 0;
  return `M${x0.toFixed(2)},${y0.toFixed(2)}` +
    `A${rOut},${rOut} 0 ${big} 1 ${x1.toFixed(2)},${y1.toFixed(2)}` +
    `L${x2.toFixed(2)},${y2.toFixed(2)}` +
    `A${rIn},${rIn} 0 ${big} 0 ${x3.toFixed(2)},${y3.toFixed(2)}Z`;
}

interface DonutProps {
  segments: Segment[];
  size?: number;
  thick?: number;
  centerValue?: string;
  centerLabel?: string;
  aria?: string;
}

export function Donut({
  segments, size = 148, thick = 17, centerValue, centerLabel = "", aria,
}: DonutProps) {
  const rOut = size / 2 - 3;
  const rIn = rOut - thick;
  const cx = size / 2;
  const cy = size / 2;
  const total = segments.reduce((a, s) => a + s.value, 0);
  const live = segments.filter((s) => s.value > 0);
  const gap = total > 0 ? 1.6 : 0;
  const { bind, tipNode } = useChartTip();

  const paths: ReactNode[] = [];
  let ang = 0;
  for (const s of live) {
    const sweep = (s.value / total) * 360;
    const a0 = ang + (live.length > 1 ? gap / 2 : 0);
    const a1 = ang + sweep - (live.length > 1 ? gap / 2 : 0);
    if (a1 > a0) {
      paths.push(
        <path
          key={s.label} className="dseg" d={arcPath(cx, cy, rOut, rIn, a0, a1)} fill={s.color}
          {...bind({
            label: s.label,
            value: fnum(s.value),
            note: `${fpct(s.value, total)} of ${fnum(total)}`,
            color: s.color,
          })}
        />,
      );
    }
    ang += sweep;
  }

  const big = centerValue ?? (total ? fpct(live[0]?.value ?? 0, total, 0) : "–");
  const fs = String(big).length > 5 ? 17 : 21;

  return (
    <>
    <svg
      className="donut" viewBox={`0 0 ${size} ${size}`} width={size} height={size}
      role="img" aria-label={aria ?? centerLabel ?? "chart"}
    >
      {total === 0 ? (
        <circle
          cx={cx} cy={cy} r={(rOut + rIn) / 2}
          fill="none" stroke="var(--s-none)" strokeWidth={rOut - rIn}
        />
      ) : paths}
      <text
        className="dc-val" x={cx} y={cy + (centerLabel ? 1 : 6)}
        textAnchor="middle" fontSize={fs}
      >
        {big}
      </text>
      {centerLabel && (
        <text className="dc-lab" x={cx} y={cy + 15} textAnchor="middle">{centerLabel}</text>
      )}
    </svg>
    {tipNode}
    </>
  );
}

export function DonutCard({
  title, subtitle, segments, centerValue, centerLabel, size, thick,
}: {
  title: string; subtitle?: string; segments: Segment[];
  centerValue?: string; centerLabel?: string; size?: number; thick?: number;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  return (
    <div className="donutbox">
      <div className="t">{title}</div>
      {subtitle && <div className="st">{subtitle}</div>}
      <Donut
        segments={segments} size={size} thick={thick}
        centerValue={centerValue} centerLabel={centerLabel} aria={title}
      />
      <div className="legend">
        {segments.map((s) => (
          <div className="li" key={s.label}>
            <span className="sw" style={{ background: s.color }} />
            <span>{s.label}</span>
            <span className="n">{fnum(s.value)}</span>
            <span className="p">{fpct(s.value, total, 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BarRow({
  label, sub, icon, segments, totalTxt,
}: {
  label: string; sub?: string; icon?: string; segments: Segment[]; totalTxt: ReactNode;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const { bind, tipNode } = useChartTip();
  return (
    <div className="bar-row">
      <div className="bl">
        {icon && <i className={`bi ${icon} bl-ico`} aria-hidden="true" />}
        <span className="bl-copy">
          {label}
          {sub && <small>{sub}</small>}
        </span>
      </div>
      <div className="bartrack">
        {segments.filter((s) => s.value > 0).map((s) => (
          <i
            key={s.label}
            style={{ width: `${((s.value / total) * 100).toFixed(2)}%`, background: s.color }}
            {...bind({
              label: s.label || label,
              value: fnum(s.value),
              note: `${fpct(s.value, total)} of ${label}`,
              color: s.color,
            })}
          />
        ))}
      </div>
      <div className="bv">{totalTxt}</div>
      {tipNode}
    </div>
  );
}

export function Kpi({
  label, value, foot, tone, icon,
}: {
  label: string; value: string; foot?: ReactNode; icon?: string;
  tone?: "done" | "await" | "est" | "none" | "alert";
}) {
  return (
    <div className={`kpi${tone ? ` k-${tone}` : ""}`}>
      {icon && <i className={`bi ${icon} kico`} aria-hidden="true" />}
      <div className="lab">{label}</div>
      <div className="val">{value}</div>
      <div className="foot">{foot}</div>
    </div>
  );
}

export function LegendStrip() {
  const items: [string, string][] = [
    ["Priced (New Rate entered)", "var(--s-done)"],
    ["Received, awaiting pricing", "var(--s-await)"],
    ["Estimation entered", "var(--s-est)"],
    ["Not yet received", "var(--s-none)"],
  ];
  return (
    <div className="rowflex" style={{ gap: 14, fontSize: 11.5, color: "var(--ink-2)" }}>
      {items.map(([label, color]) => (
        <span className="rowflex" style={{ gap: 6 }} key={label}>
          <span className="sw" style={{ background: color, width: 9, height: 9, borderRadius: 2 }} />
          {label}
        </span>
      ))}
    </div>
  );
}
